import type { CSSProperties } from "react";

import { ArrowLeftTailIcon } from "@/assets/icons";

import { FloatingArrow } from "@floating-ui/react";

import { Button } from "../ui/button";

import type { TourStep } from "./configs/types";
import { useTourFloating } from "./hooks/useTourFloating";

type Props = Readonly<{
  targetEl: Element | null;
  step: TourStep;
  stepIndex: number;
  totalSteps: number;
  isFirst: boolean;
  isLast: boolean;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  onFinish: () => void;
}>;

/**
 * A tooltip component for displaying tour step information.
 */
export function TourTooltip({
  targetEl,
  step,
  stepIndex,
  totalSteps,
  isFirst,
  isLast,
  onNext,
  onPrev,
  onSkip,
  onFinish
}: Props) {
  const { placement, placementOffset, arrowOffset } = step.tooltipConfig;
  const { refs, floatingStyles, context, arrowRef } = useTourFloating(
    targetEl,
    placement,
    placementOffset
  );

  if (!targetEl) return null;

  const resolvedArrowOffset = arrowOffset ?? DEFAULT_ARROW_OFFSET;
  const arrowStaticOffset = getArrowStaticOffset(
    placement,
    resolvedArrowOffset
  );
  const arrowStyle = getArrowOffsetStyle(
    context.placement,
    placement,
    resolvedArrowOffset
  );

  return (
    <div
      ref={refs.setFloating}
      style={floatingStyles}
      data-tour-interactive="true"
      className="z-[1012] w-max max-w-[340px] rounded-[10px] bg-[#2d2d2d] px-[18px] py-4 text-sm leading-normal text-white shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
    >
      <FloatingArrow
        ref={arrowRef}
        context={context}
        fill={TOOLTIP_BG}
        data-testid="tour-tooltip-arrow"
        staticOffset={arrowStaticOffset}
        style={arrowStyle}
      />
      <h4 className="m-0 mb-2 text-lg font-semibold">{step.title}</h4>
      <p className="m-0 mb-[18px] text-gray-300">{step.description}</p>
      <footer className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          {!isFirst && (
            <button
              type="button"
              onClick={onPrev}
              aria-label="Previous step"
              className={backBtnClass}
            >
              <ArrowLeftTailIcon />
            </button>
          )}
          <span className="text-sm text-gray-200">
            {stepIndex + 1}/{totalSteps}
          </span>
        </div>
        <div className="flex items-center gap-3.5">
          {isLast ? (
            <button
              type="button"
              onClick={onFinish}
              className={tooltipBtnClass("primary")}
            >
              Complete
            </button>
          ) : (
            <>
              <Button
                type="button"
                onClick={onSkip}
                className={tooltipBtnClass("ghost")}
                variant="ghost"
              >
                Skip
              </Button>
              <Button
                type="button"
                onClick={onNext}
                className={tooltipBtnClass("primary")}
              >
                Next
              </Button>
            </>
          )}
        </div>
      </footer>
    </div>
  );
}

const TOOLTIP_BG = "#2d2d2d";

// FloatingArrow positions the arrow against the reference center by default.
// For aligned placements, keep it near the tooltip's aligned edge instead.
const DEFAULT_ARROW_OFFSET = 18;

const backBtnClass =
  "inline-flex cursor-pointer items-center justify-center border-none bg-transparent p-0 text-gray-200";

// Primary = filled teal pill (Next/Finish); ghost = teal text link (Skip).
function tooltipBtnClass(variant: "primary" | "ghost"): string {
  const base = "cursor-pointer border-none text-sm font-medium";
  if (variant === "primary") {
    return `${base} rounded-lg bg-[#2a9d8f] px-[22px] py-2 font-semibold text-white`;
  }
  return `${base} bg-transparent p-0 text-[#2a9d8f] hover:bg-transparent hover:text-current`;
}

function getArrowStaticOffset(
  placement: TourStep["tooltipConfig"]["placement"],
  arrowOffset: NonNullable<TourStep["tooltipConfig"]["arrowOffset"]>
) {
  return placement.includes("-") ? arrowOffset : undefined;
}

function getArrowOffsetStyle(
  actualPlacement: TourStep["tooltipConfig"]["placement"],
  requestedPlacement: TourStep["tooltipConfig"]["placement"],
  arrowOffset: NonNullable<TourStep["tooltipConfig"]["arrowOffset"]>
): CSSProperties | undefined {
  const alignment = requestedPlacement.split("-")[1];
  if (alignment !== "start" && alignment !== "end") return undefined;

  const side = actualPlacement.split("-")[0];

  if (side === "top" || side === "bottom") {
    return alignment === "end"
      ? { right: arrowOffset, left: "auto" }
      : { left: arrowOffset, right: "auto" };
  }

  return alignment === "end"
    ? { bottom: arrowOffset, top: "auto" }
    : { top: arrowOffset, bottom: "auto" };
}
